import api from './axios';

// Normalize an Iranian mobile number: Persian/Arabic digits → English,
// strip spaces/dashes. Returns the cleaned string.
export const normalizePhone = (phone) => {
  if (phone === null || phone === undefined) return '';
  return String(phone)
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
    .replace(/[\s\-()]/g, '')
    .trim();
};

export const isValidIranianMobile = (phone) => {
  const normalized = normalizePhone(phone);
  return /^09\d{9}$/.test(normalized);
};

// Search for a customer by mobile number.
// Resolves to the customer object, or null when no customer exists.
// Transport/server errors stay errors; only data:null means a new customer.
export const searchCustomerByPhone = async (phone) => {
  const normalized = normalizePhone(phone);

  if (!isValidIranianMobile(normalized)) {
    throw new Error('شماره موبایل معتبر نیست (مثال: 09123456789)');
  }

  const response = await api.get('/mechanics/customers/search', {
    params: { phone: normalized },
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'خطا در جستجوی مشتری');
  }
  return response.data.data || null;
};

// Fetch all vehicles registered for a customer id.
export const getCustomerVehicles = async (customerId) => {
  if (!customerId) {
    throw new Error('شناسه مشتری مشخص نیست');
  }
  const response = await api.get(`/mechanics/customers/${customerId}/vehicles`);
  const payload = response.data;
  if (payload && payload.success && Array.isArray(payload.data)) {
    return payload.data;
  }
  throw new Error((payload && payload.message) || 'خطا در دریافت خودروهای مشتری');
};

export const createCustomer = async (phone, name) => {
  const [firstName, ...rest] = name.trim().split(/\s+/);
  const response = await api.post('/mechanics/customers', {
    phone: normalizePhone(phone), firstName, lastName: rest.join(' '),
  });
  if (!response.data?.success || !response.data.data?.id) {
    throw new Error(response.data?.message || 'خطا در ثبت مشتری');
  }
  return response.data.data;
};

// Vehicles must always belong to the selected customer.
export const registerVehicleForCustomer = async (customerId, vehicleData) => {
  if (!customerId) throw new Error('ابتدا مشتری را ثبت یا انتخاب کنید');
  const body = {
    title: vehicleData.title,
    brand: vehicleData.brand || 'Unknown',
    currentMileage: Number(vehicleData.currentMileage) || 0,
    buildYear: vehicleData.buildYear ? Number(vehicleData.buildYear) : null,
    color: vehicleData.color || null,
    engineModel: vehicleData.engineModel || null,
    chassisNumber: vehicleData.chassisNumber || null,
    plate1: vehicleData.plate1 || null,
    plate2: vehicleData.plate2 || null,
    plate3: vehicleData.plate3 || null,
    plateEnd: vehicleData.plateEnd || null,
    image: vehicleData.image || null,
  };

  const response = await api.post(`/mechanics/customers/${customerId}/vehicles`, body);
  if (response.data?.success && response.data.data) return response.data.data;
  throw new Error(response.data?.message || 'خطا در ثبت خودرو');
};

export const getCustomers = async ({ search = '', page = 1, vehicles = 'all', sort = 'newest' } = {}) => {
  const response = await api.get('/mechanics/customers', { params: { search, page, vehicles, sort } });
  if (!response.data?.success || !Array.isArray(response.data.data)) {
    throw new Error(response.data?.message || 'خطا در دریافت مشتری‌ها');
  }
  return response.data;
};

export const updateCustomer = async (customerId, data) => {
  const response = await api.patch(`/mechanics/customers/${customerId}`, data);
  if (!response.data?.success) throw new Error(response.data?.message || 'خطا در ویرایش مشتری');
  return response.data.data;
};

export const deleteCustomer = async (customerId) => {
  const response = await api.delete(`/mechanics/customers/${customerId}`);
  if (!response.data?.success) throw new Error(response.data?.message || 'خطا در حذف مشتری');
  return response.data;
};
